import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface DomainActionConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean;

  /** Dialog title, e.g. "Refund domain". */
  title: string;

  /** Explanatory body text. */
  description: string;

  /** Label for the confirm button, e.g. "Issue Refund". */
  confirmLabel: string;

  /** Confirm button variant — "destructive" for refund/cancel. */
  confirmVariant?: "default" | "destructive";

  /** Whether the confirm mutation is in flight. */
  isSubmitting?: boolean;

  /** Called when the dialog is dismissed. */
  onClose: () => void;

  /** Called with the admin-entered reason when confirmed. */
  onConfirm: (reason: string) => void;
}

/**
 * Shared confirmation dialog for the refund and cancel domain actions.
 *
 * Requires the admin to type a non-empty reason before confirming —
 * the reason is sent to the backend for the audit trail.
 */
export function DomainActionConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmVariant = "destructive",
  isSubmitting,
  onClose,
  onConfirm,
}: DomainActionConfirmDialogProps) {
  const [reason, setReason] = useState("");

  // Clear the reason whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length > 0 && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <DialogDescription>{description}</DialogDescription>
          <Label htmlFor="domain-action-reason">Reason</Label>
          <Textarea
            id="domain-action-reason"
            placeholder="Explain why this action is being taken (recorded for audit)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            disabled={!canConfirm}
            onClick={() => onConfirm(trimmed)}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
